import { useState, useMemo, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ExternalLink, Package, PlusCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminWhatnot() {
  const { data: tpl, isLoading: tplLoading } = useQuery({
    queryKey: ["admin-whatnot-templates"],
    queryFn: async () => (await api.get("/admin/whatnot/templates")).data,
  });
  const { data: ordersData, isLoading: ordersLoading, refetch: refetchOrders } = useQuery({
    queryKey: ["admin-whatnot-orders"],
    queryFn: async () => (await api.get("/admin/whatnot/orders")).data,
    refetchInterval: 30000,
  });

  const categories = tpl?.categories || [];
  const dimProfiles = tpl?.dimension_profiles || [];
  const orders = ordersData?.data || [];

  const [form, setForm] = useState({
    title: "", description: "", category_id: "", dimension_profile_id: "",
    image: "", price_usd: "", quantity: 1, publish: true,
  });
  const [attrs, setAttrs] = useState({}); // {attrId: value}
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const selectedCat = useMemo(() => (tpl?.categories || []).find((c) => c.id === form.category_id), [tpl, form.category_id]);

  // Whatnot fetches image URLs from the public internet, so relative app
  // paths (e.g. /api/img/...) must be made absolute against the backend URL.
  const absUrl = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

  const pickCategory = (id) => {
    setForm((f) => ({ ...f, category_id: id }));
    const cat = categories.find((c) => c.id === id);
    const seed = {};
    (cat?.attributes || []).forEach((a) => { seed[a.id] = a.value || ""; });
    setAttrs(seed);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  // Pre-fill from a card/print ("List on Whatnot"): title, description, image,
  // and auto-pick the sole category/shipping profile so it's near one-click.
  const location = useLocation();
  const prefillApplied = useRef(false);
  useEffect(() => {
    const pf = location.state?.prefill;
    if (!pf || prefillApplied.current || tplLoading) return;
    prefillApplied.current = true;
    setForm((f) => ({
      ...f,
      title: pf.title ?? f.title,
      description: pf.description ?? f.description,
      image: pf.image ? absUrl(pf.image) : f.image,
    }));
    const cats = tpl?.categories || [];
    const dims = tpl?.dimension_profiles || [];
    if (cats.length === 1) pickCategory(cats[0].id);
    if (dims.length === 1) setForm((f) => ({ ...f, dimension_profile_id: dims[0].id }));
    toast.info("Form pre-filled from your item — set a price and create the listing.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, tpl, tplLoading]);

  const submit = async () => {
    if (!form.title || !form.category_id || !form.dimension_profile_id || !form.image || !form.price_usd) {
      toast.error("Fill title, category, shipping profile, image URL and price.");
      return;
    }
    setBusy(true); setResult(null);
    try {
      const payload = {
        title: form.title,
        description: form.description || null,
        category_id: form.category_id,
        dimension_profile_id: form.dimension_profile_id,
        attributes: Object.entries(attrs).filter(([, v]) => v).map(([id, value]) => ({ id, value })),
        images: [absUrl(form.image)],
        price_usd: Math.round(Number(form.price_usd)),
        quantity: Math.max(1, parseInt(form.quantity, 10) || 1),
        publish: !!form.publish,
      };
      const res = (await api.post("/admin/whatnot/products", payload)).data;
      setResult(res);
      const s = res.steps || {};
      if (s.published) toast.success("Listing created & published to Whatnot");
      else if (s.priced) toast.success("Listing created & priced — publishing shortly (images processing)");
      else toast.success("Product created on Whatnot");
    } catch (e) {
      toast.error(apiError(e, "Could not create the listing"));
    } finally {
      setBusy(false);
    }
  };

  const publishNow = async (pid) => {
    try {
      await api.post(`/admin/whatnot/products/${pid}/publish`);
      toast.success("Published to Whatnot shop");
      setResult((r) => (r ? { ...r, steps: { ...r.steps, published: true } } : r));
    } catch (e) {
      toast.error(apiError(e, "Publish failed — images may still be processing"));
    }
  };

  const fmtMoney = (m) => {
    if (!m) return "—";
    if (typeof m === "object") return `${m.currency || ""} ${m.amount ?? ""}`.trim();
    return String(m);
  };

  return (
    <div data-testid="admin-whatnot" className="space-y-10">
      <div>
        <h2 className="font-display text-2xl text-white flex items-center gap-2"><Package className="h-5 w-5 text-cyan-300" /> Whatnot</h2>
        <p className="text-sm text-white/40">Create listings on your Whatnot catalog and track orders. Connected as <span className="text-cyan-300">@{tpl?.seller?.username || "…"}</span>.</p>
      </div>

      {/* Create listing */}
      <section className="rounded-2xl border border-white/10 bg-black/40 p-5" data-testid="whatnot-create-section">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2"><PlusCircle className="h-4 w-4 text-cyan-300" /> Create a listing</h3>
        {tplLoading ? (
          <div className="flex items-center text-white/50 py-6"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading catalog options…</div>
        ) : categories.length === 0 ? (
          <p className="text-sm text-amber-200/80">No category found on your account yet. Create your first product in the Whatnot app, then its category &amp; shipping profile become available here.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="text-sm text-white/70 sm:col-span-2">Title
              <Input data-testid="whatnot-title" value={form.title} onChange={set("title")} placeholder="Holographic Waifu Card #12" className="mt-1" />
            </label>
            <label className="text-sm text-white/70 sm:col-span-2">Description
              <textarea data-testid="whatnot-description" value={form.description} onChange={set("description")} rows={2}
                className="mt-1 w-full rounded-md bg-white/5 border border-white/15 px-3 py-2 text-sm text-white" placeholder="Condition, details…" />
            </label>
            <label className="text-sm text-white/70">Category
              <select data-testid="whatnot-category" value={form.category_id} onChange={(e) => pickCategory(e.target.value)}
                className="mt-1 w-full rounded-md bg-white/5 border border-white/15 px-3 py-2 text-sm text-white">
                <option value="">Select…</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="text-sm text-white/70">Shipping profile (weight)
              <select data-testid="whatnot-dimprofile" value={form.dimension_profile_id} onChange={set("dimension_profile_id")}
                className="mt-1 w-full rounded-md bg-white/5 border border-white/15 px-3 py-2 text-sm text-white">
                <option value="">Select…</option>
                {dimProfiles.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </label>
            {/* attributes for the chosen category */}
            {(selectedCat?.attributes || []).map((a) => (
              <label key={a.id} className="text-sm text-white/70">{a.name}
                <Input data-testid={`whatnot-attr-${a.id}`} value={attrs[a.id] ?? ""}
                  onChange={(e) => setAttrs((s) => ({ ...s, [a.id]: e.target.value }))} className="mt-1" />
              </label>
            ))}
            <label className="text-sm text-white/70 sm:col-span-2">Image URL (public https)
              <Input data-testid="whatnot-image" value={form.image} onChange={set("image")} placeholder="https://…/card.png" className="mt-1" />
            </label>
            <label className="text-sm text-white/70">Price (whole USD)
              <Input data-testid="whatnot-price" type="number" min="1" step="1" value={form.price_usd} onChange={set("price_usd")} placeholder="25" className="mt-1" />
            </label>
            <label className="text-sm text-white/70">Quantity
              <Input data-testid="whatnot-quantity" type="number" min="1" step="1" value={form.quantity} onChange={set("quantity")} className="mt-1" />
            </label>
            <label className="text-sm text-white/70 flex items-center gap-2 sm:col-span-2">
              <input data-testid="whatnot-publish" type="checkbox" checked={form.publish} onChange={(e) => setForm((f) => ({ ...f, publish: e.target.checked }))} />
              Publish to my Whatnot shop now
            </label>
            <div className="sm:col-span-2">
              <Button data-testid="whatnot-create-submit" onClick={submit} disabled={busy} className="bg-cyan-400 text-black hover:bg-cyan-300">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Creating…</> : "Create listing on Whatnot"}
              </Button>
              <span className="ml-3 text-[11px] text-white/40">Whatnot requires whole-dollar buy-it-now prices.</span>
            </div>
          </div>
        )}

        {result && (
          <div className="mt-4 rounded-xl border border-white/10 bg-black/50 p-4 text-sm" data-testid="whatnot-create-result">
            <div className="text-white/80">Created “{result.product?.title}”.</div>
            <div className="mt-1 flex flex-wrap gap-2 text-[11px]">
              {["created", "priced", "inventory", "published"].map((k) => (
                <span key={k} className={`px-2 py-0.5 rounded-full border ${result.steps?.[k] ? "text-emerald-300 border-emerald-400/40 bg-emerald-400/10" : "text-white/40 border-white/15"}`}>{k}</span>
              ))}
            </div>
            {!result.steps?.published && result.steps?.priced && (
              <div className="mt-2">
                <span className="text-amber-200/80 text-[12px]">Not published yet (images processing). </span>
                <Button data-testid="whatnot-publish-now" size="sm" variant="outline" onClick={() => publishNow(result.product?.id)}>Publish now</Button>
              </div>
            )}
            {result.product?.url && (
              <a href={result.product.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-cyan-300 text-[12px]">Open in Whatnot <ExternalLink className="h-3 w-3" /></a>
            )}
          </div>
        )}
      </section>

      {/* Orders */}
      <section className="rounded-2xl border border-white/10 bg-black/40 p-5" data-testid="whatnot-orders-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-white">Orders</h3>
          <Button size="sm" variant="outline" onClick={() => refetchOrders()}>Refresh</Button>
        </div>
        {ordersLoading ? (
          <div className="flex items-center text-white/50 py-6"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading orders…</div>
        ) : orders.length === 0 ? (
          <p className="text-sm text-white/40" data-testid="whatnot-orders-empty">No Whatnot orders yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="whatnot-orders-table">
              <thead><tr className="text-left text-white/40 text-[11px] uppercase tracking-wider">
                <th className="py-2 pr-4">Order</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Total</th><th className="py-2 pr-4">Placed</th>
              </tr></thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-t border-white/5" data-testid={`whatnot-order-${o.id}`}>
                    <td className="py-2 pr-4 text-white/80 font-mono text-[12px]">{o.id}</td>
                    <td className="py-2 pr-4 text-white/70">{o.status || o.state || "—"}</td>
                    <td className="py-2 pr-4 text-white/70">{fmtMoney(o.total || o.total_price || o.grand_total)}</td>
                    <td className="py-2 pr-4 text-white/50">{(o.created_at || "").slice(0, 10) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
